import { DestroyRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { debounce } from 'lodash';
import {
  CampaignInsightsService,
  DeliveryFilter,
  EXPORT_CAP,
  deliveryCriteria,
} from '../../../../../../services/campaign/campaign-insights.service';
import { IMessageReportContacts } from '../../../types/messagesTypes';
import { csvFileName, downloadCsv, toCsv } from '../../../../../../../utils/csv';
import { formatCount } from '../../../../../../../utils/sms';

/**
 * State behind a message's recipient table: which send, search, delivery
 * filter, sort and page are showing, loading the matching page of receipts
 * (with its total) and exporting every matching row as CSV.
 */
export class ReceiptTable {
  rows: IMessageReportContacts[] = [];
  loading = false;
  failed = false;
  page = 0;
  size = 20;
  total: number | null = null;

  term = '';
  filter: DeliveryFilter = 'all';
  sort = '';
  /** '' shows every send of the message. */
  scheduleUuid = '';

  exporting = false;
  exportNote = '';
  /** The note is an export failure (announced as an alert). */
  exportFailed = false;

  private sub?: Subscription;
  private exportSub?: Subscription;
  private searchLater = debounce(() => {
    this.page = 0;
    this.load();
  }, 400);

  constructor(
    private insights: CampaignInsightsService,
    destroyRef: DestroyRef,
    private messageUuid: () => string
  ) {
    destroyRef.onDestroy(() => {
      this.sub?.unsubscribe();
      this.exportSub?.unsubscribe();
      this.searchLater.cancel();
    });
  }

  get filtered(): boolean {
    return !!this.term.trim() || this.filter !== 'all';
  }

  /** From the total when the API sends one, else "one more page" while pages come back full. */
  get totalPages(): number {
    if (this.total !== null) return Math.max(1, Math.ceil(this.total / this.size));
    return this.rows.length === this.size ? this.page + 2 : this.page + 1;
  }

  /** Report criteria for the current send, search, filter and sort. */
  criteria(): Record<string, unknown> {
    const term = this.term.trim();
    // Digits (with spaces, dashes or a leading +) search the number, anything else the name.
    const isNumber = /^\+?[\d\s-]{3,}$/.test(term);
    return {
      ...(this.scheduleUuid ? { 'scheduleUuid.equals': this.scheduleUuid } : {}),
      ...(term
        ? isNumber
          ? { 'mobileNumber.contains': term.replace(/[^\d]/g, '') }
          : { 'fullName.contains': term }
        : {}),
      ...(this.sort ? { sort: this.sort } : {}),
      ...deliveryCriteria(this.filter),
    };
  }

  load() {
    const uuid = this.messageUuid();
    if (!uuid) return;
    this.sub?.unsubscribe();
    this.loading = true;
    this.failed = false;
    this.sub = this.insights
      .messageReceipts(uuid, { ...this.criteria(), page: this.page, size: this.size })
      .subscribe({
        next: ({ rows, total }) => {
          this.rows = rows;
          this.total = total;
          this.loading = false;
        },
        error: () => {
          this.rows = [];
          this.total = null;
          this.loading = false;
          this.failed = true;
        },
      });
  }

  setSearch(term: string) {
    this.term = term ?? '';
    this.searchLater();
  }

  setFilter(filter: DeliveryFilter) {
    this.filter = filter;
    this.page = 0;
    this.load();
  }

  setSort(sort: string) {
    this.sort = sort;
    this.page = 0;
    this.load();
  }

  setSchedule(uuid: string) {
    this.scheduleUuid = uuid ?? '';
    this.page = 0;
    this.load();
  }

  setPage(page: number) {
    this.page = Math.max(0, Number(page) || 0);
    this.load();
  }

  /**
   * Stores the new page size only: the pagination follows a size change with
   * changePage(0), and that loads the first page at the new size.
   */
  setSize(size: number) {
    this.size = Number(size) || 20;
    this.page = 0;
  }

  /** Every row matching the current send, search and filter, as a CSV file. */
  download(...nameParts: Array<string | null | undefined>) {
    const uuid = this.messageUuid();
    if (this.exporting || !uuid) return;
    this.exporting = true;
    this.exportNote = '';
    this.exportFailed = false;
    const criteria = this.criteria();

    this.exportSub = this.insights
      .collectAll((page, size) => this.insights.messageReceipts(uuid, { ...criteria, page, size }))
      .subscribe({
        next: (rows) => {
          this.exporting = false;
          if (!rows.length) {
            this.exportNote = 'Nothing to download: no recipients match.';
            return;
          }
          const csv = toCsv(rows, [
            { label: 'Name', value: (row) => row.fullName },
            { label: 'Mobile number', value: (row) => row.mobileNumber },
            { label: 'Group', value: (row) => row.group },
            { label: 'Gender', value: (row) => row.gender },
            { label: 'Age', value: (row) => row.age },
            { label: 'Country', value: (row) => row.country },
            { label: 'Delivery status', value: (row) => row.deliveryStatus },
            { label: 'Time', value: (row) => row.timeStamp },
          ]);
          downloadCsv(csvFileName(...nameParts, this.filter !== 'all' ? this.filter : null), csv);
          if (rows.length >= EXPORT_CAP) {
            this.exportNote = `The file holds the first ${formatCount(EXPORT_CAP)} recipients.`;
          }
        },
        error: () => {
          this.exporting = false;
          this.exportFailed = true;
          this.exportNote = 'The file could not be prepared. Check your connection and try again.';
        },
      });
  }
}
